import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Plus, Settings2, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { ResourceCanvas } from '@/components/admin/resource-canvas'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  createResourceCategory,
  deleteResource,
  deleteResourceCategory,
  getResourceCategories,
  getResources,
  updateResourceCategory,
  upsertResource,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/error-messages'
import type { Resource, ResourceCategory, ResourceCategoryVisualType } from '@/types/domain'

const categorySchema = z.object({
  name: z.string().trim().min(1, 'Bitte einen Namen eingeben.'),
  visual_type: z.enum(['grid', 'list', 'canvas']),
})

type CategoryFormValues = z.infer<typeof categorySchema>

const defaultCategoryValues: CategoryFormValues = {
  name: '',
  visual_type: 'grid',
}

export function AdminResourcesPage() {
  const queryClient = useQueryClient()
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null)
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<ResourceCategory | null>(null)

  const categoryForm = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: defaultCategoryValues,
  })

  const categoriesQuery = useQuery({
    queryKey: ['resource-categories'],
    queryFn: getResourceCategories,
  })

  const resourcesQuery = useQuery({
    queryKey: ['resources', selectedCategoryId],
    queryFn: () => getResources(selectedCategoryId || undefined),
    enabled: !!selectedCategoryId,
  })

  const saveCategoryMutation = useMutation({
    mutationFn: async (values: CategoryFormValues) => {
      const payload = {
        name: values.name.trim(),
        visual_type: values.visual_type as ResourceCategoryVisualType,
        config: editingCategory?.config ?? {},
      }

      if (editingCategory) {
        await updateResourceCategory(editingCategory.id, payload)
      } else {
        await createResourceCategory(payload)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resource-categories'] })
      setIsCategoryDialogOpen(false)
      categoryForm.reset(defaultCategoryValues)
      toast.success(editingCategory ? 'Kategorie aktualisiert' : 'Kategorie erstellt')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const deleteCategoryMutation = useMutation({
    mutationFn: deleteResourceCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resource-categories'] })
      setSelectedCategoryId(null)
      toast.success('Kategorie gelöscht')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const categories = categoriesQuery.data ?? []
  const selectedCategory = categories.find((category) => category.id === selectedCategoryId)

  const onCategorySubmit = categoryForm.handleSubmit((values) => {
    saveCategoryMutation.mutate(values)
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Ressourcen & Layouts</h1>
          <p className="text-sm text-muted-foreground">
            Verwalte Räume, Bahnen oder andere Ressourcen und deren visuelle Darstellung.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingCategory(null)
            saveCategoryMutation.reset()
            categoryForm.reset(defaultCategoryValues)
            setIsCategoryDialogOpen(true)
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          Kategorie erstellen
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        <div className="space-y-4 lg:col-span-1">
          <Card>
            <CardHeader className="p-4">
              <CardTitle className="text-sm font-medium">Kategorien</CardTitle>
            </CardHeader>
            <CardContent className="p-2 pt-0">
              <div className="space-y-1">
                {categories.map((category) => (
                  <div
                    key={category.id}
                    className={`flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${
                      selectedCategoryId === category.id
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => setSelectedCategoryId(category.id)}
                  >
                    <span className="truncate font-medium">{category.name}</span>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-7 w-7 ${
                          selectedCategoryId === category.id ? 'hover:bg-primary-foreground/10' : ''
                        }`}
                        onClick={(event) => {
                          event.stopPropagation()
                          setEditingCategory(category)
                          saveCategoryMutation.reset()
                          categoryForm.reset({
                            name: category.name,
                            visual_type: category.visual_type,
                          })
                          setIsCategoryDialogOpen(true)
                        }}
                      >
                        <Settings2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
                {categories.length === 0 && (
                  <p className="p-4 text-center text-xs text-muted-foreground">
                    Noch keine Kategorien vorhanden.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-3">
          {selectedCategory ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium">{selectedCategory.name} Konfiguration</h2>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    Typ: <span className="font-medium capitalize">{selectedCategory.visual_type}</span>
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:bg-destructive/10"
                    onClick={() => {
                      if (
                        confirm(
                          'Möchtest du diese Kategorie und alle zugehörigen Ressourcen wirklich löschen?',
                        )
                      ) {
                        deleteCategoryMutation.mutate(selectedCategory.id)
                      }
                    }}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Löschen
                  </Button>
                </div>
              </div>

              <ResourceCanvas
                category={selectedCategory}
                resources={resourcesQuery.data ?? []}
                onUpdateResource={async (resource: Resource) => {
                  try {
                    await upsertResource(resource)
                    queryClient.invalidateQueries({ queryKey: ['resources', selectedCategory.id] })
                    toast.success('Ressource erfolgreich gespeichert.')
                  } catch {
                    toast.error('Fehler beim Speichern der Ressource')
                  }
                }}
                onDeleteResource={async (id: string) => {
                  try {
                    await deleteResource(id)
                    queryClient.invalidateQueries({ queryKey: ['resources', selectedCategory.id] })
                    toast.success('Ressource erfolgreich gelöscht.')
                  } catch {
                    toast.error('Fehler beim Löschen der Ressource')
                  }
                }}
                onAddResource={async (name: string) => {
                  try {
                    await upsertResource({
                      category_id: selectedCategory.id,
                      name,
                      order_index: (resourcesQuery.data?.length ?? 0) + 1,
                      x: 0,
                      y: 0,
                    })
                    queryClient.invalidateQueries({ queryKey: ['resources', selectedCategory.id] })
                    toast.success('Ressource erfolgreich erstellt.')
                  } catch {
                    toast.error('Fehler beim Erstellen der Ressource')
                  }
                }}
              />
            </div>
          ) : (
            <Card className="flex h-[400px] flex-col items-center justify-center border-dashed text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Settings2 className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="mt-4 text-sm font-medium">Keine Kategorie ausgewählt</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Wähle links eine Kategorie aus oder erstelle eine neue, um Ressourcen zu verwalten.
              </p>
            </Card>
          )}
        </div>
      </div>

      <Dialog
        open={isCategoryDialogOpen}
        onOpenChange={(nextOpen) => {
          setIsCategoryDialogOpen(nextOpen)
          if (!nextOpen) {
            categoryForm.reset(defaultCategoryValues)
            saveCategoryMutation.reset()
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Kategorie bearbeiten' : 'Neue Kategorie'}</DialogTitle>
            <DialogDescription>
              Gib der Kategorie einen Namen und wähle den Darstellungs-Typ.
            </DialogDescription>
          </DialogHeader>

          <Form {...categoryForm}>
            <form onSubmit={onCategorySubmit} className="grid gap-4 py-4">
              <FormField
                control={categoryForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="cat-name">Name</FormLabel>
                    <FormControl>
                      <Input
                        id="cat-name"
                        placeholder="z.B. Kursräume, Schwimmbahnen"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={categoryForm.control}
                name="visual_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="cat-type">Darstellungs-Typ</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger id="cat-type">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="grid">Grid (Liste von Karten)</SelectItem>
                        <SelectItem value="list">Klassische Liste</SelectItem>
                        <SelectItem value="canvas">Freies Layout (Canvas)</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCategoryDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={saveCategoryMutation.isPending}>
                  {saveCategoryMutation.isPending ? 'Speichere ...' : 'Speichern'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
